import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import Pagination from '@mui/material/Pagination';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useDebounce from '../../../resources/hooks/UseDebounce';
import { thirdPartyApi } from '../../../resources/hooks/api/thirdPartyApiHook';
import { calculateMaxHeightContent } from '../../../resources/functions';
import { getBranch } from '../../../store/user/selectors';
import '../../Transactions/filter.css';
import './QBCustomerList.css';

type SortField =
  | 'displayName'
  | 'companyName'
  | 'createdAt'
  | 'email'
  | 'phone'
  | 'qboCustomerId'
  | 'balance'
  | 'updatedAt';
type SortDirection = 'asc' | 'desc';

const DEFAULT_SORT = {
  sortBy: 'updatedAt' as SortField,
  sortOrder: 'desc' as SortDirection,
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const EMPTY_VALUE = '--';

interface CustomerRecord {
  id: number;
  Id_qbo?: string | null;
  DisplayName?: string | null;
  CompanyName?: string | null;
  GivenName?: string | null;
  FamilyName?: string | null;
  FullyQualifiedName?: string | null;
  PrimaryEmailAddr_Address?: string | null;
  PrimaryPhone_FreeFormNumber?: string | null;
  Active?: number | boolean | string | null;
  Balance?: number | string | null;
  BalanceWithJobs?: number | string | null;
  Job?: number | boolean | string | null;
  BillAddr_Line1?: string | null;
  BillAddr_City?: string | null;
  BillAddr_CountrySubDivisionCode?: string | null;
  BillAddr_PostalCode?: string | null;
  BillAddr_Country?: string | null;
  BillAddr?: {
    Line1?: string | null;
    City?: string | null;
    CountrySubDivisionCode?: string | null;
    PostalCode?: string | null;
    Country?: string | null;
  } | null;
  ShipAddr_Line1?: string | null;
  ShipAddr_City?: string | null;
  ShipAddr_CountrySubDivisionCode?: string | null;
  ShipAddr_PostalCode?: string | null;
  ShipAddr_Country?: string | null;
  MetaData_CreateTime?: string | null;
  MetaData_LastUpdatedTime?: string | null;
}

const formatOptionalText = (value?: string | number | null): string => {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  const normalizedText = String(value).trim();
  return normalizedText.length > 0 ? normalizedText : EMPTY_VALUE;
};

const formatBalance = (balance?: number | string | null): string => {
  if (balance === null || balance === undefined || balance === '') {
    return '$0.00';
  }

  const numericValue = Number(balance);
  if (Number.isNaN(numericValue)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const formatPhoneNumber = (phone?: string | null): string => {
  if (!phone) {
    return EMPTY_VALUE;
  }

  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return phone.trim() || EMPTY_VALUE;
};

const formatCustomerDate = (dateValue?: string | null): string => {
  if (!dateValue) {
    return EMPTY_VALUE;
  }

  const parsedDate = moment(dateValue);
  return parsedDate.isValid() ? parsedDate.format('MM/DD/YYYY') : EMPTY_VALUE;
};

const isCustomerActive = (activeValue?: number | boolean | string | null): boolean => {
  if (typeof activeValue === 'boolean') {
    return activeValue;
  }

  if (typeof activeValue === 'number') {
    return activeValue === 1;
  }

  if (typeof activeValue === 'string') {
    const normalized = activeValue.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'active';
  }

  return true;
};

const SortIndicatorIcon: React.FC<{ direction: SortDirection | null }> = ({
  direction,
}) => {
  if (direction === 'asc') {
    return (
      <span className="table-header-sort-icon ms-1" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M6 2.5L9.5 6.5H2.5L6 2.5Z"
            fill="currentColor"
          />
        </svg>
      </span>
    );
  }

  if (direction === 'desc') {
    return (
      <span className="table-header-sort-icon ms-1" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M6 9.5L2.5 5.5H9.5L6 9.5Z"
            fill="currentColor"
          />
        </svg>
      </span>
    );
  }

  return (
    <span className="table-header-sort-icon table-header-sort-icon--neutral ms-1" aria-hidden="true">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
      >
        <path
          d="M6 2.5L8.5 5H3.5L6 2.5ZM6 9.5L3.5 7H8.5L6 9.5Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
};

const QBCustomerList: React.FC = () => {
  const { t } = useTranslation('scenes.thirdparty.quickbook.QBCustomerList');
  const { qbFetchCustomers } = thirdPartyApi();
  const branchData = useSelector(getBranch);

  const [records, setRecords] = useState<CustomerRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortField>(DEFAULT_SORT.sortBy);
  const [sortOrder, setSortOrder] = useState<SortDirection>(DEFAULT_SORT.sortOrder);
  const [limit, setLimit] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);

  useEffect(() => {
    const updateContentHeight = () => {
      setContentMaxHeight(calculateMaxHeightContent(126));
    };

    updateContentHeight();
    window.addEventListener('resize', updateContentHeight);

    return () => window.removeEventListener('resize', updateContentHeight);
  }, []);

  useDebounce(
    () => {
      const nextSearchTerm = searchInput.trim();
      setPage(1);
      setSearchTerm((prevValue) =>
        prevValue === nextSearchTerm ? prevValue : nextSearchTerm
      );
    },
    [searchInput],
    400
  );

  useEffect(() => {
    if (!branchData?.id) {
      setRecords([]);
      setTotalRecords(0);
      return;
    }

    setIsLoading(true);

    const params: Record<string, string | number> = {
      branch_id: +branchData['id'],
      page,
      limit,
      sortBy,
      sortOrder: sortOrder,
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    qbFetchCustomers(
      params,
      (message: string, resp: any) => {
        const customerResponse = resp?.data?.data || {};

        setRecords(customerResponse.items || customerResponse.result || []);
        setTotalRecords(Number(customerResponse.total || 0));
        setIsLoading(false);
      },
      (message: string) => {
        setRecords([]);
        setTotalRecords(0);
        setIsLoading(false);
        toast.error(message || t('messages.fetch_error'));
      }
    );
  }, [branchData?.id, limit, page, searchTerm, sortBy, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(totalRecords / limit),
    [limit, totalRecords]
  );

  const getSortDirection = (field: SortField) =>
    sortBy === field ? sortOrder : null;

  const handleSort = (field: SortField) => {
    setPage(1);

    if (sortBy !== field) {
      setSortBy(field);
      setSortOrder('asc');
      return;
    }

    if (sortOrder === 'asc') {
      setSortOrder('desc');
      return;
    }

    setSortBy(DEFAULT_SORT.sortBy);
    setSortOrder(DEFAULT_SORT.sortOrder);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const paginationCount = Math.max(totalPages, 1);

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid">
        <div className="pb-3 pt-3" id="ux_page_title">
          <div className="align-items-center d-flex justify-content-between gap-3 flex-wrap qbo-customer-list__title-row">
            <div className="align-items-center d-flex page-title">
              <h1 className="h5 font-weight-700 mb-0 d-inline-flex">
                {t('page.title')}
              </h1>
            </div>
            <Form.Group
              className="filter-box mb-0 qbo-customer-list__search-group"
              controlId="qboCustomerSearch"
            >
              <div className="qbo-customer-list__search-field">
                <span className="qbo-customer-list__search-icon" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M11.742 10.344 15 13.6 13.6 15l-3.256-3.258a6 6 0 1 1 1.398-1.398ZM6.667 11.333a4.667 4.667 0 1 0 0-9.333 4.667 4.667 0 0 0 0 9.333Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <Form.Control
                  type="text"
                  className="qbo-customer-list__search-input"
                  placeholder={t('filter.search_placeholder')}
                  value={searchInput}
                  onChange={handleSearchChange}
                />
              </div>
            </Form.Group>
          </div>
        </div>

        <div className="executive-dashboard">
          <div className="dashboard-top-part">
            <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
              <div className="loader"></div>
            </div>

            <div
              className="table-responsive list-table table-scroll qbo-customer-list__table-scroll"
              style={{ maxHeight: contentMaxHeight || undefined }}
            >
              <table className="table table-default">
                <thead>
                  <tr className="align-middle">
                    <th className="text-start">
                      <button
                        type="button"
                        className="table-header-sort-btn"
                        onClick={() => handleSort('qboCustomerId')}
                      >
                        <span>{t('table.headers.customer_id')}</span>
                        <SortIndicatorIcon direction={getSortDirection('qboCustomerId')} />
                      </button>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.qbo_imported')}</span>
                    </th>
                    <th className="text-start">
                      <button
                        type="button"
                        className="table-header-sort-btn"
                        onClick={() => handleSort('displayName')}
                      >
                        <span>{t('table.headers.display_name')}</span>
                        <SortIndicatorIcon direction={getSortDirection('displayName')} />
                      </button>
                    </th>
                    <th className="text-start">
                      <button
                        type="button"
                        className="table-header-sort-btn"
                        onClick={() => handleSort('companyName')}
                      >
                        <span>{t('table.headers.company_name')}</span>
                        <SortIndicatorIcon direction={getSortDirection('companyName')} />
                      </button>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.first_name')}</span>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.last_name')}</span>
                    </th>
                    <th className="text-center">
                      <span>{t('table.headers.status')}</span>
                    </th>
                    <th className="text-end">
                      <button
                        type="button"
                        className="table-header-sort-btn justify-content-end w-100"
                        onClick={() => handleSort('balance')}
                      >
                        <span>{t('table.headers.balance')}</span>
                        <SortIndicatorIcon direction={getSortDirection('balance')} />
                      </button>
                    </th>
                    <th className="text-start">
                      <button
                        type="button"
                        className="table-header-sort-btn"
                        onClick={() => handleSort('phone')}
                      >
                        <span>{t('table.headers.phone')}</span>
                        <SortIndicatorIcon direction={getSortDirection('phone')} />
                      </button>
                    </th>
                    <th className="text-start">
                      <button
                        type="button"
                        className="table-header-sort-btn"
                        onClick={() => handleSort('email')}
                      >
                        <span>{t('table.headers.email')}</span>
                        <SortIndicatorIcon direction={getSortDirection('email')} />
                      </button>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.address_line_1')}</span>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.city')}</span>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.state')}</span>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.zip_code')}</span>
                    </th>
                    <th className="text-start">
                      <span>{t('table.headers.country')}</span>
                    </th>
                    <th className="text-center">
                      <button
                        type="button"
                        className="table-header-sort-btn table-header-sort-btn--center"
                        onClick={() => handleSort('createdAt')}
                      >
                        <span>{t('table.headers.created_at')}</span>
                        <SortIndicatorIcon direction={getSortDirection('createdAt')} />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.length > 0 ? (
                    records.map((record: CustomerRecord) => (
                      <tr className="align-middle h-60" key={record.id}>
                        <td className="text-start">{formatOptionalText(record.Id_qbo)}</td>
                        <td className="text-start">{t('common.yes')}</td>
                        <td className="text-start fw-600">
                          {formatOptionalText(record.DisplayName)}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(record.CompanyName)}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(record.GivenName)}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(record.FamilyName)}
                        </td>
                        <td className="text-center">
                          <Badge
                            bg={isCustomerActive(record.Active) ? 'success' : 'secondary'}
                          >
                            {isCustomerActive(record.Active)
                              ? t('status.active')
                              : t('status.inactive')}
                          </Badge>
                        </td>
                        <td className="text-end">{formatBalance(record.Balance)}</td>
                        <td className="text-start">
                          {formatPhoneNumber(record.PrimaryPhone_FreeFormNumber)}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(record.PrimaryEmailAddr_Address)}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(
                            record.BillAddr_Line1 ?? record.BillAddr?.Line1
                          )}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(
                            record.BillAddr_City ?? record.BillAddr?.City
                          )}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(
                            record.BillAddr_CountrySubDivisionCode ??
                              record.BillAddr?.CountrySubDivisionCode
                          )}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(
                            record.BillAddr_PostalCode ?? record.BillAddr?.PostalCode
                          )}
                        </td>
                        <td className="text-start">
                          {formatOptionalText(
                            record.BillAddr_Country ?? record.BillAddr?.Country
                          )}
                        </td>
                        <td className="text-center">
                          {formatCustomerDate(record.MetaData_CreateTime)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={16} className="text-center">
                        {t('table.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="qbo-customer-list__pagination-row">
              <div className="qbo-customer-list__page-size">
                <span className="qbo-customer-list__page-size-label">
                  {t('pagination.rows_per_page')}
                </span>
                <Form.Select
                  size="sm"
                  className="qbo-customer-list__page-size-select"
                  value={limit}
                  onChange={handlePageSizeChange}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="qbo-customer-list__pagination-control">
                <Pagination
                  count={paginationCount}
                  page={page}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  size="medium"
                  disabled={!totalRecords}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QBCustomerList;
